import {
  GET_ROLES,
  getVisibleWorkspaces,
  useChangeWorkspace,
  useCurrentAccount,
  useFindByQuery
} from '@gripp/shared-logic';
import { CardButton, useSlideLayout } from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { Colors } from 'libs/shared-ui/src/themes';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

const MY_HISTORY = 'MY_HISTORY';
const MY_ACTIVITY_LABEL = 'topNav.myHistory';

export const WorkspacesUser = () => {
  const { switchViewMode } = useSlideLayout();
  const { account, isLoading } = useCurrentAccount();
  const { t } = useTranslation();
  const { data, isLoading: isLoadingRoles } = useFindByQuery({
    modelName: 'role',
    query: GET_ROLES,
  });

  const queryClient = useQueryClient();
  const { workspaceRoles } = account || {};

  const { mutateAsync: changeWorkspace, isPending } = useChangeWorkspace();

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(
    account?.workspace?.id || MY_HISTORY
  );

  useEffect(() => {
    if (account?.workspace?.id) setSelectedWorkspaceId(account.workspace.id);
  }, [account]);

  if (!account?.allowedWorkspaces || account?.allowedWorkspaces?.length < 1) {
    return null;
  }

  // Workspaces where the caller's resolved role is Guest are hidden here so a
  // guest never learns the shape of an organisation from having replied to
  // one conversation; showing MY_HISTORY alone (below) is the deliberate
  // degraded state when every allowed workspace is one they're a guest of.
  const visibleWorkspaces = getVisibleWorkspaces(
    account.allowedWorkspaces,
    account.workspaceRoles,
    data?.items
  );

  const handleSelectItem = async (wId: string) => {
    setSelectedWorkspaceId(wId);

    if (!account?.id) {
      return;
    }

    const workspaceId = wId === MY_HISTORY ? undefined : wId;

    await changeWorkspace({
      input: {
        id: account?.id,
        workspaceId,
      },
    });

    await queryClient.invalidateQueries({ queryKey: ['currentAccount'] });

    // TODO: Figure out query invalidation for gripp admin going to my activity
    window.location.replace('/settings');
  };

  const getRole = (workspaceId: string): string | undefined => {
    const workspaceRole = workspaceRoles?.find(
      ({ workspace }) => workspace === workspaceId
    );

    if (
      !workspaceRole ||
      !data ||
      !data.items ||
      !Array.isArray(data.items)
    ) {
      return undefined;
    }

    return Array.isArray(data.items) 
      ? data.items.find(({ id }: { id: string }) => id === workspaceRole?.role)?.name 
      : undefined;
  };

  const isCurrentWorkspace = (workspaceId: string) => {
    return selectedWorkspaceId === workspaceId;
  };

  const setIsActive = (workspaceId: string) => {
    // Active if it's the current workspace and either no specific page is set or we're on the workspace page
    return isCurrentWorkspace(workspaceId);
  }

  const setIcon = (workspaceId: string) => {
    return isCurrentWorkspace(workspaceId) ? 
      'Check' : 
      'Barn A';
  }

  return (
    <View style={styles.container}>
      <Text variant='bodyLarge'>{t('preferences.settings.workSpaces')}</Text>
      {isLoading || isPending || isLoadingRoles ? (
        <ActivityIndicator
          animating={true}
          color={Colors.primary}
          size={30}
        />
      ) : (
        [
          { id: MY_HISTORY, name: t(MY_ACTIVITY_LABEL) },
          ...visibleWorkspaces,
        ].map(({ id, name }) => {
          const role = getRole(id);

          return (
            <CardButton 
              key={id}
              active={setIsActive(id)}
              showAvatars={false}
              leftIcon={setIcon(id)}
              text={name}
              text2={role}
              showText2={!!role}
              onPress={() => handleSelectItem(id)}
              bordered={false}
            />
          );
        })
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: 12,
  },
});